import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Grid, Button, Divider, Card, TextField, CircularProgress } from '@mui/material';
import { FETCH_CART, GET_SHOPIFY_LISTING } from '../../graphql/queries';
import { useMutation, useQuery } from '@apollo/client';
import { ShopifyImage } from '../../graphql/generated';
import { CREATE_CART } from '../../graphql/mutations';
import { toast } from 'react-toastify';
import ProductPageSkeleton from '../../Components/Skeletons/ProductPageSkeleton';
import Cookies from 'js-cookie';

const ListingDetailsPage = () => {
  const { listingId } = useParams(); // Retrieve product ID from URL
  const location = useLocation();
  const navigate = useNavigate();
  const { shop_domain } = location.state || {}; // Extract shop_domain from state
  const fakeData = {
    getShopifyListing: {
      id: "gid://shopify/Product/123",
      title: "Blue T-Shirt",
      product_type: "Tops",
      vendor: "BrandA",
      shop_domain: "store.myshopify.com",
      images: [
        { src: "https://via.placeholder.com/200x200.png?text=Blue+T-Shirt", altText: "Blue T-Shirt" },
        { src: "https://via.placeholder.com/200x200.png?text=Alt+View", altText: "Alt View" }
      ],
      options: [
        { id: "opt1", name: "Size", values: ["S", "M", "L"] },
        { id: "opt2", name: "Color", values: ["Blue", "Red"] }
      ],
      variants: [
        {
          id: "gid://shopify/ProductVariant/111",
          price: 25,
          inventoryQuantity: 5,
          selectedOptions: [
            { name: "Size", value: "S" },
            { name: "Color", value: "Blue" }
          ]
        }
      ]
    }
  }

  const { loading, error, data = fakeData } = useQuery(GET_SHOPIFY_LISTING, {
    variables: { shop_domain, id: listingId },
    skip: !shop_domain, // Skip the query if shop_domain is not available
  });

  const [selectedQuantity, setSelectedQuantity] = useState(1);

  const handleQuantityChange = (event: any) => {
    const value = Number(event.target.value);
    if (value >= 1 && value <= selectedVariant?.inventoryQuantity) setSelectedQuantity(value); // Prevent negative values or zero
    if (value > selectedVariant?.inventoryQuantity) setSelectedQuantity(selectedVariant?.inventoryQuantity)
  };

  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [selectedVariant, setSelectedVariant] = useState<any | null>(null);
  const [disabledOptions, setDisabledOptions] = useState<Record<string, Set<string>>>({});
  const [addCartLoading, setLoading] = useState<boolean>(false);
  const [checkoutLoading, setCheckoutLoading] = useState<boolean>(false);


  const [createCart] = useMutation(CREATE_CART);
  // const [createCart] = useMutation(CREATE_CART);

  const handleAddToCart = async (e: any, load: boolean = true) => {
    e.preventDefault();
    // const cartId = "dummy"; // Check for existing cartId in local storage
    setLoading(load);
    try {
      // if (cartId) {

      await createCart({
        variables: {
          items: [
            {
              merchandiseId: `${selectedVariant.id}`,
              quantity: selectedQuantity,
            },
          ],
        },
        refetchQueries: [FETCH_CART]
      });

      // } else {
      //   // Create a new cart
      //   const response = await createCart({
      //     variables: {
      //       items: [
      //         {
      //           merchandiseId: `${selectedVariant.id}`,
      //           quantity: selectedQuantity,
      //         },
      //       ],
      //     },
      //   });

      //   const newCart = response.data.createCart;

      //   // Save the new cart ID to local storage
      //   localStorage.setItem('shopifyCartId', newCart.id);
      // }
      toast.success('Item added to cart!');
    } catch (error: any) {
      console.error('Error adding to cart:', error.message);
      toast.error('Failed to add item to cart. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCheckout = async (e: any) => {
    e.preventDefault();
    setCheckoutLoading(true);
    await handleAddToCart(e, false);
    setCheckoutLoading(false);
    navigate("/cart")
  }


  // Update selectedOptions and selectedVariant when the product loads
  useEffect(() => {
    if (data?.getShopifyListing) {
      const product = data.getShopifyListing;

      // Initialize selectedOptions to the first value of each option
      const initialSelectedOptions: Record<string, string> = {};
      product.options.forEach((option: any) => {
        if (option.values.length > 0) {
          initialSelectedOptions[option.name] = option.values[0];
        }
      });

      setSelectedOptions(initialSelectedOptions);
      updateSelectedVariant(product.variants, initialSelectedOptions);
    }
  }, [data]);

  useEffect(() => {
    if (shop_domain) {
      Cookies.set("shopDomain", shop_domain);
    }
  }, [shop_domain]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [listingId]);

  // Function to update the selectedVariant based on selectedOptions
  const updateSelectedVariant = (variants: any[], selectedOptions: Record<string, string>) => {
    const matchedVariant = variants.find((variant: any) =>
      variant.selectedOptions.every(
        (option: any) => selectedOptions[option.name] === option.value
      )
    );
    setSelectedVariant(matchedVariant || null);
  };

  // Function to update disabled options based on inventory and selectedOptions
  const updateDisabledOptions = (
    variants: any[],
    newlySelectedOption: Record<string, string>,
    options: any[]
  ) => {
    const newDisabledOptions: Record<string, Set<string>> = {};

    options.forEach((option: any) => {
      const currentOptionName = option.name;

      // Skip the current row (already selected)
      if (newlySelectedOption[currentOptionName]) {
        return;
      }

      const disabledValues = new Set<string>();

      // Iterate over the values in this option
      option.values.forEach((value: string) => {
        // Check if this value would match any variant with inventory > 0
        const isValueAvailable = variants.some((variant: any) => {
          return (
            variant.inventoryQuantity > 0 &&
            variant.selectedOptions.every((variantOption: any) => {
              // Match selected options or this option value
              return (
                (variantOption.name === currentOptionName && variantOption.value === value) ||
                (selectedOptions[variantOption.name] === variantOption.value)
              );
            })
          );
        });

        if (!isValueAvailable) {
          disabledValues.add(value);
        }
      });

      newDisabledOptions[currentOptionName] = disabledValues;
    });

    setDisabledOptions(newDisabledOptions);
  };

  // Handle option selection
  const handleOptionSelect = (optionName: string, value: string) => {
    const updatedOptions = { ...selectedOptions, [optionName]: value };
    setSelectedOptions(updatedOptions);

    // Update selectedVariant based on the new options
    if (data?.getShopifyListing?.variants) {
      updateSelectedVariant(data.getShopifyListing.variants, updatedOptions);
      updateDisabledOptions(
        data.getShopifyListing.variants,
        { [optionName]: value },
        data.getShopifyListing.options
      );
    }
  };

  if (loading) {
    return <ProductPageSkeleton />;
  }

  if (error) {
    return <Typography>Error: {error.message}</Typography>;
  }

  const product = data.getShopifyListing;

  if (!product) {
    return <Typography>No product details available.</Typography>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        padding: 3,
        backgroundColor: '#fff',
        margin: '20px auto',
        borderRadius: '10px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
      }}
    >
      {/* Product Images Section */}
      <Grid container spacing={2}>
        {/* Image Stack */}
        <Grid item xs={2}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
              border: '1px solid #ddd',
              borderRadius: 2,
              width: 124,
              overflow: 'hidden',
              justifyContent: 'center',
            }}
          >
            {product.images.map((image: ShopifyImage, index: number) => (
              <Box
                key={index}
                sx={{
                  cursor: 'pointer',
                  border: selectedImage === image.src ? '2px solid #1976d2' : 'none',
                  borderRadius: '4px',
                  width: 120,
                  height: 180,
                  justifyContent: 'center',
                  alignContent: 'center',
                  overflow: 'hidden',
                }}
                onClick={() => setSelectedImage(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.altText || 'Product Thumbnail'}
                  width="100%"
                  style={{ borderRadius: '4px' }}
                />
              </Box>
            ))}
          </Box>
        </Grid>

        {/* Large Displayed Image */}
        <Grid item xs={5}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              borderRadius: '4px',
              border: '1px solid #ddd',
            }}
          >
            <img
              src={selectedImage || product.images[0]?.src}
              alt="Selected Product"
              style={{ maxHeight: '400px', maxWidth: '100%', borderRadius: '4px' }}
            />
          </Box>
        </Grid>

        {/* Product Details Card */}
        <Grid item xs={5}>
          <Card sx={{ padding: 3 }}>
            {/* Category Information */}
            <Typography variant="body2" color="textSecondary">
              {product.product_type || 'Category'} / {product.vendor || 'Brand'}
            </Typography>

            {/* Product Title */}
            <Typography variant="h5" sx={{ marginTop: 1 }}>
              {product.title}
            </Typography>

            {/* Price */}
            <Typography variant="h6" sx={{ marginTop: 1, marginBottom: 2 }}>
              ${selectedVariant ? selectedVariant.price : 'N/A'}
            </Typography>

            <Divider />

            {/* Product Options */}
            {product.options.map((option: any) => (
              <Box key={option.id} sx={{ marginTop: 2 }}>
                <Typography variant="body1" sx={{ marginBottom: 1 }}>
                  {option.name}:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {option.values.map((value: string, valueIndex: number) => (
                    <Button
                      key={`${option.id}-${valueIndex}`}
                      variant={selectedOptions[option.name] === value ? 'contained' : 'outlined'}
                      disabled={
                        disabledOptions[option.name]?.has(value) // Disable if unavailable
                      }
                      onClick={() => handleOptionSelect(option.name, value)}
                      sx={{ textTransform: 'none' }}
                    >
                      {value}
                    </Button>
                  ))}
                </Box>
              </Box>
            ))}

            {/* Action Buttons */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                marginTop: 3,
                flexDirection: "column",
              }}
            >

              <Typography variant="body1" sx={{ marginBottom: 1 }} alignSelf={"start"} textAlign={"start"}>
                {selectedVariant?.inventoryQuantity || ""} available
              </Typography>
              {/* Quantity Selector */}
              <TextField
                label="Quantity"
                type="number"
                variant="outlined"
                size="small"
                value={selectedQuantity}
                onChange={handleQuantityChange}
                inputProps={{ min: 1, max: selectedVariant?.inventoryQuantity || 1 }} // Prevents negative numbers
                sx={{ width: "100px" }} // Adjust width as needed
              />

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: "row",
                }}>

                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={!selectedVariant || addCartLoading} // Disable if no valid variant
                  onClick={handleAddToCart}
                  sx={{ mr: 1 }}
                >
                  {
                    addCartLoading && (
                      <CircularProgress size={24} sx={{ color: "white", mr: 2 }} />
                    )
                  }
                  <span>Add to Bag</span>
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled={!selectedVariant || checkoutLoading} // Disable if no valid variant
                  onClick={handleCheckout}
                >
                  {
                    checkoutLoading && (
                      <CircularProgress size={24} sx={{ mr: 2 }} />
                    )
                  }
                  <span>Checkout</span>
                </Button>
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ListingDetailsPage;
